import { ChatCircleTextIcon, CheckIcon, ClockIcon, PhoneIcon } from '@phosphor-icons/react'
import { OpenInWindow } from 'iconoir-react'
import { ReactElement, useState } from 'react'
import { useQuery } from 'urql'
import { FormCard } from 'app/components/activities/FormCard'
import { ImportCard } from 'app/components/activities/ImportCard'
import { CALL_QUERY, InboundCallCard } from 'app/components/activities/InboundCallCard'
import { OutboundCallCard } from 'app/components/activities/OutboundCallCard'
import Button from 'app/components/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { ActivityJson } from 'app/types/entities'
import { SNOOZE_OPTIONS } from './responseQueue'
import { ResponseRow } from './useResponseQueue'

/**
 * What one waiting person needs, drawn the way the contact drawer draws it.
 *
 * The dispatch below is `ActivityInfo.getActivityCard`'s, deliberately: a
 * voicemail should look and behave the same whether it is read from the drawer
 * or from here, down to which cards open themselves. Those cards are where the
 * refinement lives. Every time this pane has tried to summarise them instead,
 * it has ended up saying less with more markup, and it has ended up wrong: one
 * such rewrite printed the AI summary as the whole pane, which on a missed call
 * is the one field that never varies ("nobody picked up", on all 149 of them).
 * The card already knew not to lead with that.
 *
 * `hideHeader` is the only concession. The modal states who, when and which
 * channel above it, and the card's own header would say all three again on
 * every touch.
 */

/**
 * Every verb the queue offers, as the focus flow's floating action bar.
 *
 * The same toolbar, not a lookalike: `Button` in the shared modes, the same
 * 16px bold glyph with `mr-1.5` before each label, and the same white bar that
 * hugs its buttons rather than stretching, so it reads as a toolbar over the
 * record instead of a second panel under it. Two queues that ask the same
 * question of the same person should not offer it in two different shapes.
 *
 * The one bar, and the whole story. A callback used to end here in a choice of
 * four logged outcomes, on top of the header's own done and snooze, which was
 * two clusters saying "act on this person" in different vocabularies. The bar
 * now reads as the job itself: the two ways to reach them, the two ways to put
 * the row down, and the detour to the whole record fenced off at the end.
 *
 * Rendered by the modal rather than inside the record, deliberately, and for
 * the reason the focus card learned: inside, the card's own padding squeezes it
 * and the labels wrap the moment one of them grows.
 */
export function ResponseActions({
  row,
  onText,
  onSnooze,
  onDone,
  onOpenContact,
}: {
  row: ResponseRow
  onText: () => void
  onSnooze: (until: Date) => void
  onDone: () => void
  onOpenContact?: () => void
}) {
  const phone = row.contact?.cellPhone ?? row.phone
  // A text is answered in its own thread, so its bar carries no Text button:
  // the composer is already on screen. Ringing them back instead still stands.
  const canText = row.channel !== `sms` && Boolean(phone)

  return (
    // Rounded-xl rather than a pill, as on the focus card: a pill's corner arc
    // curves in further than the square-cornered buttons at each end.
    <div className="mx-auto flex w-fit max-w-full flex-wrap items-center justify-center gap-1.5 rounded-xl border border-default bg-surface-raised p-2 shadow-elevation-4 [&_button]:shrink-0 [&_button]:whitespace-nowrap sm:flex-nowrap">
      {phone && (
        // The focus bar's own shape for a plain dial: the anchor does the
        // calling, the button inside it does the looking.
        <a href={`tel:${phone}`}>
          <Button mode="white">
            <PhoneIcon size={16} weight="bold" className="mr-1.5" />
            Call
          </Button>
        </a>
      )}
      {/* Blue for the suggested move, matching the focus bar, where the
          outlined style is what says "reach for this one". */}
      {canText && (
        <Button mode="secondary" onClick={onText}>
          <ChatCircleTextIcon size={16} weight="bold" className="mr-1.5" />
          Text
        </Button>
      )}
      <DropdownMenu>
        {/* The span is the trigger because `Button` forwards no ref; the click
            bubbles up to it, the way the focus card's snooze menu does it. */}
        <DropdownMenuTrigger asChild>
          <span>
            <Button mode="white">
              <ClockIcon size={16} weight="bold" className="mr-1.5" />
              Snooze
            </Button>
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center">
          {SNOOZE_OPTIONS.map((option) => (
            <DropdownMenuItem key={option.label} onSelect={() => onSnooze(option.at())}>
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {/* Done is the generic "dealt with it", so it rides on every channel: a
          form or a booking has no call to end, and even a text, answered in
          its own thread, still needs a way to say the row is finished. It
          dismisses the row and the queue moves on. */}
      <Button mode="white" onClick={onDone}>
        <CheckIcon size={16} weight="bold" className="mr-1.5" />
        Done
      </Button>
      {onOpenContact && (
        <>
          <div className="mx-0.5 hidden h-6 w-px bg-(--line-strong) sm:block" />
          {/* Icon-only and fenced off behind the rule, as Profile rides at the
              end of the focus flow's bar: opening the drawer is a detour from
              the queue rather than one of its verbs. */}
          <TooltipWrapper message="Open contact">
            <Button mode="white" onClick={onOpenContact} aria-label="Open contact">
              <OpenInWindow width={16} height={16} />
            </Button>
          </TooltipWrapper>
        </>
      )}
    </div>
  )
}

/**
 * The record itself: their latest touch, as the contact drawer draws it.
 *
 * A text has no separate body: the conversation and the place you answer it
 * are the same thing, so the modal renders that one instead. This covers the
 * channels whose answer is a decision rather than a message.
 */
export function ResponseDetail({ row }: { row: ResponseRow }) {
  // One clip at a time, as the drawer's feed does it.
  const [playing, setPlaying] = useState<number | string>()

  // The latest touch, and only that one.
  //
  // A row is one person, so somebody who rang three times arrives as one line,
  // and drawing all three records stacked the same tabs, the same summary and
  // the same identified-people panel three times over. The latest is the one
  // that decides whether to ring back; the rest is history, and history is
  // what the contact is for. The earlier attempts are still counted in the
  // queue, so nobody is hidden, only the repetition is.
  const latest = row.activities[row.activities.length - 1]

  // `isFirst` and `isLast` are both set on every card below: this is a single
  // record rather than a feed of one, so the timeline rail draws no line
  // above it or below it.
  const card = (activity: ActivityJson) => {
    switch (activity.type) {
      case `Call`:
        return activity.subType === `outbound` ? (
          <OutboundCallCard
            hideHeader
            activity={activity}
            contact={row.contact}
            currentAudioPlaying={playing}
            onAudioPlay={() => setPlaying(activity.id)}
            isFirst
            isLast
          />
        ) : (
          <InboundCallCard
            hideHeader
            alwaysOpen
            activity={activity}
            contact={row.contact}
            currentAudioPlaying={playing}
            onAudioPlay={() => setPlaying(activity.id)}
            isFirst
            isLast
          />
        )
      case `Form`:
        return <FormCard hideHeader alwaysOpen activity={activity} isFirst isLast />
      case `Chat`:
      case `Imported Form`:
      case `Virtual Consultation`:
      case `Online Scheduler`:
        return (
          <ImportCard hideHeader activity={activity} isFirst isLast alwaysOpen={activity.type === `Online Scheduler`} />
        )
      default:
        return null
    }
  }

  if (!latest) {
    return <p className="text-body-sm text-minimal">Nothing further was recorded about this request.</p>
  }

  // Bare, no well of its own. The record fills whatever white surface it is
  // handed, the way it fills the drawer, and drawing a second border inside
  // the modal's card left it a picture-in-a-picture with dead margins.
  if (latest.type === `Call` && latest.typeId) {
    return <CallGate id={latest.typeId}>{card(latest)}</CallGate>
  }
  return card(latest)
}

/**
 * The record's shape with nothing in it yet: the player row, the tabs, some
 * summary lines and the identified-people panel, as pulsing blanks in the
 * same bordered well the loaded record draws. Shaped and sized like the real
 * thing so the swap to the loaded card reads as the record filling in rather
 * than the modal changing its mind about its own height.
 */
function CallGate({ id, children }: { id: string; children: ReactElement | null }) {
  const [{ data, fetching }] = useQuery({
    query: CALL_QUERY,
    variables: { id },
  })

  if (fetching && !data?.call) return <RecordSkeleton />
  return children
}

function RecordSkeleton() {
  return (
    <div className="rounded-lg border border-default bg-surface-raised px-4 py-3 shadow-sm">
      <div className="flex items-center gap-2">
        <span className="h-7 w-7 shrink-0 animate-pulse rounded-full bg-surface-muted" />
        <span className="h-7 w-7 shrink-0 animate-pulse rounded-full bg-surface-muted" />
        <span className="h-6 min-w-0 grow animate-pulse rounded bg-surface-muted" />
        <span className="h-4 w-20 shrink-0 animate-pulse rounded bg-surface-muted" />
      </div>
      <div className="mt-4 flex gap-5 border-b border-subtle pb-2.5">
        <span className="h-4 w-16 animate-pulse rounded bg-surface-muted" />
        <span className="h-4 w-20 animate-pulse rounded bg-surface-muted" />
        <span className="h-4 w-20 animate-pulse rounded bg-surface-muted" />
      </div>
      <div className="mt-4 space-y-2.5">
        <span className="block h-3 w-24 animate-pulse rounded bg-surface-muted" />
        <span className="block h-3 w-4/5 animate-pulse rounded bg-surface-muted" />
        <span className="block h-3 w-28 animate-pulse rounded bg-surface-muted" />
        <span className="block h-3 w-2/5 animate-pulse rounded bg-surface-muted" />
        <span className="block h-3 w-32 animate-pulse rounded bg-surface-muted" />
        <span className="block h-3 w-1/4 animate-pulse rounded bg-surface-muted" />
      </div>
      <div className="mt-5 h-16 animate-pulse rounded-md bg-surface-muted/70" />
    </div>
  )
}
